import React, { createRef } from "react"
import { loc } from "@/_services/localization"
import { FormControl, InputGroup } from "react-bootstrap"
import Select, { components } from "react-select"
import { domAttributes, getPhoneNumberInfo, handleAccessibleOnKeyDown } from "@/_services/utils"
import { getList } from "@/_services/lists"
import ButtonWithTooltip from "@/_components/ButtonWithTooltip"

const createIndexedItems = values => {
  const items = {}
  for (const item of values || []) {
    items[item.value] = item
  }
  return items
}

class CountrySelectWrapper extends React.Component {
  constructor(props) {
    super(props)
    this.containerRef = createRef()
    this.state = {
      isInputFocused: false,
      countryOptions: [],
      inputValue: "",
      selectValue: { value: "" },
      isLoading: true,
      indexedItems: {},
    }
  }

  componentDidMount() {
    this.update()
  }

  update() {
    const { countryCallingCodesList, value } = this.props

    const setStateFromList = list => {
      if (!list?.values?.length) {
        return
      }
      const { localNumber, callingCode } = getPhoneNumberInfo(value, list.values)
      this.setState({
        countryOptions: list.values,
        isLoading: false,
        inputValue: localNumber,
        selectValue: callingCode,
        indexedItems: list.items || createIndexedItems(list.values),
      })
    }

    const list = getList(countryCallingCodesList, setStateFromList)
    setStateFromList(list)
  }

  componentWillUnmount() {
    this.containerRef.current?.removeEventListener("mousedown", this.onDomClick)
  }

  onDomClick = e => {
    const menu = this.containerRef.current?.querySelector(".select__menu")
    const containerHasTarget = this.containerRef.current?.contains(e.target)
    const menuHasTarget = menu?.contains(e.target)

    if (!containerHasTarget || !menu || !menuHasTarget) {
      this.setState({ isInputFocused: false })
    }
  }

  handleInputFocus = () => {
    this.setState({ isInputFocused: true })
  }

  handleChange = (from, event) => {
    const { onChange } = this.props

    this.setState(
      {
        ...(from === "select" ? { selectValue: event } : {}),
        ...(from === "input" ? { inputValue: event?.target?.value } : {}),
      },
      () => {
        const { selectValue, inputValue, indexedItems } = this.state
        const fullValue = `${selectValue?.value || ""}${inputValue || ""}`
        const selectedCountry = indexedItems?.[selectValue?.value]

        onChange({
          target: { value: fullValue },
          countryRegExp: selectedCountry?.regExp,
          callingCode: selectValue?.value,
          localNumber: inputValue,
        })
      },
    )
  }

  handleClear = event => {
    event?.preventDefault()
    event?.stopPropagation()
    const { onChange } = this.props
    this.setState(
      {
        inputValue: "",
        selectValue: { value: "" },
      },
      () => onChange({ target: { value: "" } }),
    )
  }

  render() {
    const { fieldName, disabled, modelFieldPath, label, className, placeholder, onClick, onKeyDown, onBlur, onFocus, hideClear } = this.props
    const { isInputFocused, inputValue, selectValue, isLoading, countryOptions } = this.state

    const selectProps = {
      selectValue,
      isDisabled: disabled,
    }

    return (
      <div className="country-select-wrapper">
        <div className="country-select-container" ref={this.containerRef}>
          <Select
            {...selectProps}
            options={countryOptions}
            components={{
              MenuList,
              ValueContainer,
              SingleValue,
            }}
            classNamePrefix={`${fieldName}-select`}
            placeholder=""
            closeMenuOnSelect={true}
            name={`${fieldName}-emoji`}
            value={selectValue}
            onChange={ev => {
              this.setState({ isInputFocused: false })
              this.handleChange("select", ev)
            }}
            onInputFocus={this.handleInputFocus}
            onInputChange={this.handleInputChange}
            menuIsOpen={isInputFocused || undefined}
            isSearchable={false}
          />
        </div>
        <InputGroup data-disabled={disabled}>
          <InputGroup.Addon className="form-input-addon country-code-addon">
            <span className="country-code-value">{selectValue?.value || ""}</span>
          </InputGroup.Addon>
          <FormControl
            onChange={ev => this.handleChange("input", ev)}
            disabled={disabled || isLoading}
            value={inputValue}
            id={modelFieldPath}
            {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
            aria-label={label}
            className={`${className || ""} country-phone-input`.trim()}
            type="tel"
            inputMode="tel"
            pattern=".*[0-9]*"
            bsClass="form-control"
            placeholder={placeholder}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onClick={onClick}
          />
          {!hideClear && !disabled && !isLoading && (inputValue || selectValue?.value) && (
            <InputGroup.Addon
              className="form-input-addon c-pointer bg-transparent country-clear-addon"
              data-test="empty-input-icon-btn"
              tabIndex="0"
              onClick={this.handleClear}
              onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: this.handleClear })}
            >
              <ButtonWithTooltip className="icn-xmark icn-xs select-btn" btnClassName="m-0 pd-0 flex-center min-w-24px min-h-24px" />
            </InputGroup.Addon>
          )}
        </InputGroup>
      </div>
    )
  }
}

export default CountrySelectWrapper

const MenuList = props => {
  const { inputValue, onInputChange, onInputFocus } = props.selectProps

  return (
    <div>
      <input
        title="non-search-field"
        className="w-100 pd-theme border-0 bbw-1"
        type="text"
        placeholder={loc("Search")}
        value={inputValue}
        onMouseDown={e => {
          e.stopPropagation()
          e.target.focus()
        }}
        onFocus={onInputFocus}
        onChange={e =>
          onInputChange(e.currentTarget.value, {
            action: "input-change",
            prevInputValue: inputValue,
          })
        }
      />
      <components.MenuList {...props} selectProps={props.selectProps} />
    </div>
  )
}

const ValueContainer = ({ children, selectProps, ...props }) => {
  const { ValueContainer } = components

  return (
    <ValueContainer {...props} selectProps={selectProps}>
      {React.Children?.map(children, child => (child ? child : null))}
    </ValueContainer>
  )
}

const SingleValue = ({ selectProps, ...props }) => {
  const { SingleValue } = components
  const { selectValue } = selectProps

  return (
    <SingleValue {...props} selectProps={selectProps}>
      <i className={`icn-flag-cnty-${(selectValue?.value2 || "").toLowerCase()} flag-cnty`} />
    </SingleValue>
  )
}
